import React from 'react';
import { Modal, List, ListItem, ListItemText, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { Button } from '../atoms/ButtonWithAuth';
import { IconButton } from '../atoms/IconButtonWithAuth';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Close, Warning, Done, GetApp } from '@material-ui/icons';
import FileUploadButton from '../atoms/FileUploadButtonWithAuth';
import Redux from '../../redux/ReduxConnector';
import { ifBasePath } from '../../config/baseConfig';
import Encoding from 'encoding-japanese';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    container: {
      background: 'var(--color-white)',
      borderRadius: 10,
      textAlign: 'left',
      width: 460,
      position: 'relative'
    },
    closeButton: {
      color: 'var(--color-white)',
      position: 'absolute',
      fontSize: 20,
      fontWeight: 900,
      top: 10,
      right: 16
    },
    heading: {
      borderRadius: '10px 10px 0 0',
      color: 'var(--color-white)',
      backgroundColor: 'var(--color-admin-key)',
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '29px',
      letterSpacing: '0.5px',
      padding: '20px'
    },
    root: {
      width: 'calc(100% - 40px)',
      padding: '0 20px 20px 20px'
    },
    listItem: {
      lineHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      marginTop: 16
    },
    button: {
      backgroundColor: '#E0E0E0',
      borderRadius: 999,
      marginLeft: 20,
      padding: '8px 12px 8px'
    },
    buttonsContainer: {
      marginTop: 12,
      textAlign: 'right'
    },
    cancelButton: {
      borderRadius: 999,
      padding: '8px 20px',
      color: 'var(--color-gray-1)',
      backgroundColor: 'var(--color-white)',
      marginRight: 16,
      '&:hover': {
        backgroundColor: '#eee'
      }
    },
    progress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translateY(-50%) translateX(-50%)'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #E0E0E0',
      marginBottom: 16
    },
    tab: {
      padding: '12px 24px',
      fontSize: 14,
      cursor: 'pointer',
      color: 'var(--color-gray-1)'
    },
    activeTab: {
      padding: '12px 24px',
      fontSize: 14,
      cursor: 'pointer',
      color: 'var(--color-admin-key)',
      borderBottom: '2px solid var(--color-admin-key)',
      fontWeight: 500
    },
    uploadDescription: {
      fontSize: 14,
      color: 'var(--color-text)',
      marginBottom: 8
    },
    dragArea: {
      border: '1px dashed #BDBDBD',
      borderRadius: 4,
      padding: '40px 16px',
      textAlign: 'center' as const,
      color: 'var(--color-text)',
      marginBottom: 16
    },
    dragMainText: {
      fontSize: 16,
      marginBottom: 8
    },
    dragSubText: {
      fontSize: 12,
      color: 'var(--color-gray-1)',
      marginBottom: 8
    },
    selectFileText: {
      fontSize: 14,
      color: 'var(--color-admin-key)'
    },
    dragInnerButton: {
      marginTop: 12
    },
    templateLinkContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: 16
    },
    templateLink: {
      fontSize: 14,
      color: 'var(--color-admin-key)',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center'
    },
    templateIcon: {
      marginLeft: 4,
      fontSize: 18
    },
    uploadButtonWrapper: {
      marginLeft: 16,
      display: 'flex',
      alignItems: 'center'
    },
    previewTableContainer: {
      minHeight: 235,
      maxHeight: 235,
      overflowY: 'auto',
      border: '1px solid #E0E0E0',
      borderRadius: 4
    },
    previewHeaderCell: {
      fontSize: 12,
      color: 'var(--color-text)',
      backgroundColor: '#FAFAFA'
    },
    previewCell: {
      fontSize: 12,
      color: 'var(--color-text)'
    },
    previewErrorText: {
      fontSize: 12,
      color: 'var(--color-error)'
    },
    terminateButton: {
      backgroundColor: 'var(--color-admin-key)',
      borderRadius: 999,
      padding: '8px 20px',
      marginRight: '10px',
      color: 'var(--color-white)',
      '&:hover': {
        backgroundColor: '#2F80ED'
      },
      '&.Mui-disabled': {
        backgroundColor: '#E0E0E0',
        color: 'var(--color-gray-1)'
      }
    }
  })
);

interface Props {
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
  isError: boolean;
  setIsError: (e: any) => void;
  processing: boolean;
  setProcessing: (value: boolean) => void;
  isSuccess: boolean;
  setIsSuccess: (e: any) => void;
}

const CsvUploadModal: React.FC<Props> = props => {
  const { isOpen, setIsOpen, isError, setIsError, processing, setProcessing, isSuccess, setIsSuccess } = props;
  const [fileInputRef, setFileInputRef] = React.useState<any>('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<'upload' | 'preview'>('upload');
  const [previewRows, setPreviewRows] = React.useState<any[]>([]);

  const styles = useStyles({});

  // モーダルを開いたときの初期化（タブとファイル選択状態など）
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab('upload');
      setFileInputRef('');
      setPreviewRows([]);
      setIsError(false);
      setIsSuccess(false);
      setErrorMessage('');
      setProcessing(false);
    }
  }, [isOpen, setActiveTab, setFileInputRef, setPreviewRows, setIsError, setIsSuccess, setErrorMessage, setProcessing]);

  const restState = React.useCallback(() => {
    setIsOpen(false);
    setIsError(false);
    setIsSuccess(false);
    setFileInputRef('');
    setErrorMessage('');
    setProcessing(false);
    setActiveTab('upload');
    setPreviewRows([]);
  }, [
    setIsOpen,
    setIsError,
    setIsSuccess,
    setFileInputRef,
    setErrorMessage,
    setProcessing,
    setActiveTab,
    setPreviewRows
  ]);

  const handleFileSelect = React.useCallback(
    (file: any) => {
      setFileInputRef(file);

      if (!file) {
        setPreviewRows([]);
        return;
      }

      // 選択したCSVファイルを簡易パースしてプレビュー用データに反映
      if (file instanceof File) {
        const reader = new FileReader();
        reader.onload = () => {
          const result = typeof reader.result === 'string' ? reader.result : '';
          if (!result) {
            setPreviewRows([]);
            return;
          }

          const lines = result.split(/\r?\n/).filter(line => line.trim() !== '');
          if (lines.length <= 1) {
            setPreviewRows([]);
            return;
          }

          const dataLines = lines.slice(1); // 先頭行はヘッダー(contractId,cancelContractDate)
          const rows = dataLines.map(line => {
            const [rawContractId, rawCancelContractDate] = line.split(',');
            const contractId = (rawContractId || '').trim();
            const cancelContractDate = (rawCancelContractDate || '').trim();

            const errors: string[] = [];

            if (!contractId) {
              errors.push('契約IDが未入力です。');
            }

            if (!cancelContractDate) {
              errors.push('解約日が未入力です。');
            } else if (!/^\d{4}\/\d{2}\/\d{2}$/.test(cancelContractDate)) {
              errors.push('解約日はYYYY/MM/DD形式で入力してください。');
            }

            const hasError = errors.length > 0;

            return {
              result: hasError ? errors.join(' ') : 'OK',
              contractId,
              cancellationDate: cancelContractDate,
              isError: hasError
            };
          });

          setPreviewRows(rows);
          setActiveTab('preview');
        };

        reader.onerror = () => {
          setPreviewRows([]);
        };

        reader.readAsText(file);
      }
    },
    [setFileInputRef, setPreviewRows, setActiveTab]
  );

  // テンプレートダウンロード処理
  const handleDownloadTemplate = React.useCallback(() => {
    const csvContent = [
      'contractId,cancelContractDate',
      '説明：全件ダウンロードの契約ID,説明：解約日(YYYY/MM/DD)',
      '例:YIfblSYsjc0ZttduR3up,例:2026/03/31',
    ].join('\n');

    // SJISにエンコードしてCSVを作成
    const sjisArray = Encoding.convert(csvContent, {
      to: 'SJIS',
      from: 'UNICODE',
      type: 'array'
    }) as number[];
    const uint8Array = new Uint8Array(sjisArray);
    const blob = new Blob([uint8Array], { type: 'text/csv;charset=shift_jis;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'withdraw_upload_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleDownloadErrorCsv = React.useCallback(async () => {
    try {
      const endpoint = `${ifBasePath}application/csv/withdrawal-queue/errors`;
      const headers = {
        Accept: 'text/csv',
        'x-api-key': Redux.getStore().user.accessToken
      };

      const res = await fetch(endpoint, {
        method: 'GET',
        headers,
        mode: 'cors'
      });

      if (!res.ok) {
        throw new Error(`ステータスコード: ${res.status}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // 解約エラーファイル名
      link.download = 'withdraw_upload_errors.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setIsError(true);
      setErrorMessage(
        `エラーCSVをダウンロードできませんでした。${String(error).replace(/^Error:\s*/, '')}`
      );
    }
  }, [setIsError, setErrorMessage]);

  const handleupload = React.useCallback(async () => {
    setProcessing(true);
    setIsError(false);
    setIsSuccess(false);

    if (!fileInputRef) {
      setIsError(true);
      setProcessing(false);
      setErrorMessage('CSVファイルはアップロードされていません。');
      return;
    }

    try {
      const endpoint = `${ifBasePath}application/csv/withdrawal-queue`;
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'text/csv',
        'x-api-key': Redux.getStore().user.accessToken
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: fileInputRef,
        mode: 'cors'
      });

      if (!res.ok) {
        throw new Error(`ステータスコード: ${res.status}`);
      }

      // サーバー側のレスポンスボディは利用せず、正常終了のみ確認
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      setErrorMessage(`失敗しました。${String(error).replace(/^Error:\s*/, '')}`);
    } finally {
      setProcessing(false);
    }
  }, [fileInputRef, setIsError, setIsSuccess, setProcessing, setErrorMessage]);

  const hasPreviewError = previewRows.some((row: any) => row.isError);
  const isRegisterDisabled = processing || previewRows.length === 0 || hasPreviewError || isSuccess;

  return (
    <Modal
      aria-labelledby='simple-modal-title'
      aria-describedby='simple-modal-description'
      open={isOpen}
      className={styles.modal}
      onClose={() => setIsOpen(false)}
    >
      <div className={styles.container}>
        <IconButton className={styles.closeButton} onClick={() => restState()}>
          <Close />
        </IconButton>
        <h2 className={styles.heading}>一括解約申請</h2>
        <div className={styles.root}>
          <div className={styles.tabs}>
            <div
              className={activeTab === 'upload' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('upload')}
            >
              ファイルアップロード
            </div>
            <div
              className={activeTab === 'preview' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('preview')}
            >
              プレビュー
            </div>
          </div>

          {activeTab === 'upload' && (
            <>
              <div className={styles.uploadDescription}>
                CSVに記載されている契約IDのすべての契約が解約されます。解約処理は翌朝の5時以降に実行されます。
              </div>
              <div className={styles.templateLinkContainer}>
                <span className={styles.templateLink} onClick={handleDownloadTemplate}>
                  CSVテンプレート
                  <GetApp className={styles.templateIcon} />
                </span>
              </div>
              <List component='nav'>
                <ListItem className={styles.listItem} divider={true}>
                  <ListItemText>CSV選択</ListItemText>
                  <div className={styles.uploadButtonWrapper}>
                    <FileUploadButton
                      onFileSelect={handleFileSelect}
                      setIsError={setIsError}
                      setIsSuccess={setIsSuccess}
                    />
                  </div>
                </ListItem>
              </List>
              <div className={styles.uploadDescription}>
                解約処理に失敗した契約をCSVとしてダウンロードできます。
              </div>
              <div className={styles.templateLinkContainer}>
                <span className={styles.templateLink} onClick={handleDownloadErrorCsv}>
                  エラーCSVダウンロード
                  <GetApp className={styles.templateIcon} />
                </span>
              </div>
            </>
          )}

          {activeTab === 'preview' && (
            <>
              <div className={styles.previewTableContainer}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.previewHeaderCell}>結果</TableCell>
                      <TableCell className={styles.previewHeaderCell}>契約ID</TableCell>
                      <TableCell className={styles.previewHeaderCell}>解約日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className={styles.previewCell}>
                          {row.result || ''}
                        </TableCell>
                        <TableCell className={styles.previewCell}>{row.contractId || ''}</TableCell>
                        <TableCell className={styles.previewCell}>{row.cancellationDate || ''}</TableCell>
                      </TableRow>
                    ))}
                    {previewRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className={styles.previewCell}>
                          プレビューできるデータがありません。
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          <AlertMessage 
            open={isError} 
            level={'アラート'} 
            title={'アップロードできません。'} 
            message={errorMessage} 
          />
          <SuccessMessage
            open={isSuccess}
            level={'success'}
            title={'登録完了しました。'}
            message={'反映までお待ちください。\n反映時刻は翌朝5時以降となります。'}
          />
          <div className={styles.buttonsContainer}>
            <Button className={styles.terminateButton} onClick={handleupload} disabled={isRegisterDisabled}>
              登録
            </Button>
            <Button className={styles.cancelButton} onClick={() => restState()}>
              キャンセル
            </Button>
          </div>
        </div>
        {processing && (
          <div className={styles.progress}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Modal>
  );
};

const useAlertStyles = makeStyles(() =>
  createStyles({
    error: {
      width: 'calc(100% - 32px)',
      height: 60,
      backgroundColor: 'var(--color-error)',
      color: 'var(--color-white)',
      padding: 16,
      margin: '10px 0',
      fontSize: 14
    },
    success: {
      width: 'calc(100% - 32px)',
      minHeight: 60,
      backgroundColor: 'var(--color-bitkey)',
      color: 'var(--color-white)',
      padding: 16,
      margin: '10px 0',
      fontSize: 14
    },
    container: {
      position: 'relative',
      top: -16,
      left: -16
    },
    icon: {
      position: 'absolute',
      top: 16,
      left: 16
    },
    title: {
      position: 'absolute',
      top: 16,
      left: 52
    },
    message: {
      position: 'absolute',
      top: 45,
      left: 52,
      whiteSpace: 'pre-wrap'
    }
  })
);

interface P {
  open: boolean;
  level: string;
  title: string;
  message: string;
}

const AlertMessage: React.FC<P> = props => {
  const { open, title, message } = props;

  const styles = useAlertStyles();

  return (
    <>
      {open && (
        <div className={styles.error}>
          <div className={styles.container}>
            <div className={styles.icon}>
              <Warning />
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.message}>{message}</div>
          </div>
        </div>
      )}
    </>
  );
};

const SuccessMessage: React.FC<P> = props => {
  const { open, title, message } = props;

  const styles = useAlertStyles();

  return (
    <>
      {open && (
        <div className={styles.success}>
          <div className={styles.container}>
            <div className={styles.icon}>
              <Done />
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.message}>{message}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default CsvUploadModal;
